import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api, { setAccessToken } from '../services/api';
import { API_ROUTES } from '../config/api';
import { User } from '../types/api.types';
import { clearPersistedDatabase } from './DatabaseContext';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  error: string | null;
  setUser: (user: User | null) => void;
  setAuthenticatedUser: (user: User) => void;
  isAuthChecking: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAuthChecking, setIsAuthChecking] = useState<boolean>(true);
  const hasInitialized = useRef<boolean>(false);
  const refreshTimerRef = useRef<number | null>(null);

  const scheduleProactiveRefresh = (access: string) => {
    try {
      const payload = JSON.parse(atob(access.split('.')[1]));
      const expMs = payload.exp * 1000;
      const delay = Math.max(0, expMs - Date.now() - 60000); // 1 minute before
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      refreshTimerRef.current = window.setTimeout(async () => {
        try {
          await api.post(API_ROUTES.AUTH.REFRESH_TOKEN, { refresh: '' });
          // interceptor updates access token via cookie-backed refresh
        } catch (e) {
          // Ignore; interceptor will handle redirect on failure
        }
      }, delay);
    } catch {
      // ignore decoding errors
    }
  };

  useEffect(() => {
    // Prevent multiple auth checks
    if (hasInitialized.current) {
      return;
    }

    const checkAuth = async () => {
      hasInitialized.current = true;
      try {
        // Try to refresh using cookie (silent login)
        const refreshResp = await api.post(API_ROUTES.AUTH.REFRESH_TOKEN, { refresh: '' });
        const access = refreshResp.data?.access as string | undefined;
        if (access) {
          setAccessToken(access);
          scheduleProactiveRefresh(access);
          const response = await api.get(API_ROUTES.AUTH.USER_INFO);
          setUser(response.data);
          setIsAuthenticated(true);
        } else {
          setAccessToken(null);
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (err) {
        setAccessToken(null);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsAuthChecking(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      setError(null);
      const response = await api.post(API_ROUTES.AUTH.LOGIN, { username, password });
      const access = response.data?.access as string;
      setAccessToken(access);
      scheduleProactiveRefresh(access);
      setIsAuthenticated(true);

      const userResponse = await api.get(API_ROUTES.AUTH.USER_INFO);
      setUser(userResponse.data);
      
      return;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur de connexion');
      console.error('Login error:', err);
      throw err;
    }
  };

  const logout = () => {
    // Call logout API but don't wait for it - clear state immediately
    // Add catch handler to prevent unhandled promise rejections
    api.post(API_ROUTES.AUTH.LOGOUT, {}).catch(() => {
      // Ignore errors - we're logging out locally regardless of API response
    });
    setAccessToken(null);
    setIsAuthenticated(false);
    setUser(null);
    setIsAuthChecking(false); // Ensure auth checking state is cleared after logout
    hasInitialized.current = false; // Reset initialization flag so auth check can run again if needed
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    // Clear persisted database selection on logout
    clearPersistedDatabase();
  };

  const setAuthenticatedUser = (user: User) => {
    setUser(user);
    setIsAuthenticated(true);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, error, setUser, setAuthenticatedUser, isAuthChecking }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
